// src/config/cities.config.ts
// Registry of deployed cities — single source of truth for URL slugs, GPS coords, and SEO metadata.
// Will be absorbed into zones.config.ts during multi-zone expansion (PRD-multi-zone-expansion).

export interface CityConfig {
  slug: string;          // URL slug: "hiriketiya"
  label: string;         // Display name: "Hiriketiya"
  country: string;       // "Sri Lanka"
  latitude: number;
  longitude: number;
  description: string;   // SEO description (English)
  image?: string;        // Background image path (V2)
}

export const CITIES: CityConfig[] = [
  {
    slug: 'hiriketiya',
    label: 'Hiriketiya',
    country: 'Sri Lanka',
    latitude: 5.96318194,
    longitude: 80.70767406,
    description: 'Beach yoga, surf sessions, sunset parties and local workshops',
  },
  {
    slug: 'mirissa',
    label: 'Mirissa',
    country: 'Sri Lanka',
    latitude: 5.948,
    longitude: 80.452,
    description: 'Whale watching, beach bars, live music and cultural events',
  },
  {
    slug: 'weligama',
    label: 'Weligama',
    country: 'Sri Lanka',
    latitude: 5.974,
    longitude: 80.430,
    description: 'Surf camps, food markets, art exhibitions and community events',
  },
  {
    slug: 'ahangama',
    label: 'Ahangama',
    country: 'Sri Lanka',
    latitude: 5.9742,
    longitude: 80.3625,
    description: 'Surf hubs, beach yoga, sunset bars and cozy cafés',
  },
  {
    slug: 'arugam-bay',
    label: 'Arugam Bay',
    country: 'Sri Lanka',
    latitude: 6.8378,
    longitude: 81.8267,
    description: 'World-class surf, full moon parties, beach shacks and east-coast vibes',
  },
];

export const DEFAULT_CITY = CITIES[0]; // Hiriketiya

/** Find a city by its URL slug. Returns undefined if not found. */
export function getCityBySlug(slug: string): CityConfig | undefined {
  return CITIES.find(c => c.slug === slug.toLowerCase());
}

/** All valid city slugs (for route validation). */
export const CITY_SLUGS = CITIES.map(c => c.slug);

/** Distance threshold (km) below which a user is considered "in zone" of a city.
 *  Above this, we don't auto-redirect the user — we show the cities index instead. */
export const CITY_PROXIMITY_THRESHOLD_KM = 50;

/** Haversine distance between two GPS points in kilometers. */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Find the deployed city closest to the given GPS coordinates.
 *  Returns null if no cities configured. Returns city + distance in km. */
export function findNearestCity(latitude: number, longitude: number): { city: CityConfig; distanceKm: number } | null {
  if (CITIES.length === 0) return null;
  let nearest = CITIES[0];
  let minDist = haversineDistance(latitude, longitude, nearest.latitude, nearest.longitude);
  for (let i = 1; i < CITIES.length; i++) {
    const d = haversineDistance(latitude, longitude, CITIES[i].latitude, CITIES[i].longitude);
    if (d < minDist) {
      minDist = d;
      nearest = CITIES[i];
    }
  }
  return { city: nearest, distanceKm: minDist };
}
